import {
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileText,
  ArrowLeft,
} from 'lucide-react'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { useCreateSupportTicket } from '@/hooks/useCpn'
import { authService } from '@/services/auth'
import { CpnSupportTicketType, cpnSupportTicketTypes } from '@/types/cpn'
import { issueTypeLabels } from '@/utils/support'

interface SupportTicketModalProps {
  paymentId: string
  onClose: () => void
  onBack?: () => void
}

const SupportTicketModal = ({
  paymentId,
  onClose,
  onBack,
}: SupportTicketModalProps) => {
  const authData = authService.getAuthData()
  const userEmail = authData?.email || ''

  const [selectedIssueType, setSelectedIssueType] = useState<
    CpnSupportTicketType | ''
  >('')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState(userEmail)
  const [ccEmails, setCcEmails] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [createdTicket, setCreatedTicket] = useState<{
    id: string
    caseRefId: string
    issueType: CpnSupportTicketType
    email: string
    ccEmails?: string[]
  } | null>(null)

  const createTicketMutation = useCreateSupportTicket()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedIssueType || !description.trim() || !email.trim()) {
      return
    }

    const ccEmailsList = ccEmails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    createTicketMutation.mutate(
      {
        paymentId,
        issueType: selectedIssueType,
        description: description.trim(),
        email: email.trim(),
        ...(ccEmailsList.length > 0 && { ccEmails: ccEmailsList }),
      },
      {
        onSuccess: (data) => {
          setCreatedTicket(data)
          setIsSubmitted(true)
        },
      }
    )
  }

  const handleClose = () => {
    onClose()
  }

  if (isSubmitted && createdTicket) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="w-full max-w-sm h-[600px] bg-white dark:bg-gray-800 rounded-lg border-0 flex flex-col">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Ticket Created
                </h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">
                  Support ticket created successfully!
                </h3>
                <div className="space-y-2 text-sm text-green-800 dark:text-green-200">
                  <p>
                    <span className="font-medium">Ticket ID:</span>{' '}
                    {createdTicket.id}
                  </p>
                  <p>
                    <span className="font-medium">Case Reference:</span>{' '}
                    {createdTicket.caseRefId}
                  </p>
                  <p>
                    <span className="font-medium">Issue Type:</span>{' '}
                    {issueTypeLabels[createdTicket.issueType].label}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span>{' '}
                    {createdTicket.email}
                  </p>
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>
                  You will receive email updates at{' '}
                  <strong>{createdTicket.email}</strong> regarding the status of
                  your support ticket.
                </p>
                {createdTicket.ccEmails &&
                  createdTicket.ccEmails.length > 0 && (
                    <p className="mt-2">
                      CC recipients: {createdTicket.ccEmails.join(', ')}
                    </p>
                  )}
              </div>

              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-sm h-[600px] bg-white dark:bg-gray-800 rounded-lg border-0 flex flex-col overflow-hidden">
        <div className="p-6 flex flex-col h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {onBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="p-1 mr-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <FileText className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create Support Ticket
              </h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4 flex-1 min-h-0 flex flex-col"
          >
            <div className="flex-1 space-y-4 overflow-y-auto min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment ID
                </label>
                <input
                  type="text"
                  value={paymentId}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Issue Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedIssueType}
                  onChange={(e) =>
                    setSelectedIssueType(e.target.value as CpnSupportTicketType)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select an issue type</option>
                  {cpnSupportTicketTypes.map((type) => (
                    <option key={type} value={type}>
                      {issueTypeLabels[type].label}
                    </option>
                  ))}
                </select>
                {selectedIssueType && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {issueTypeLabels[selectedIssueType].description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please provide detailed information about the issue..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={4}
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {description.length}/500 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CC Emails <span className="text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Separate multiple emails with commas
                </p>
              </div>
            </div>

            {createTicketMutation.error && (
              <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">
                    Failed to create support ticket
                  </span>
                </div>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {createTicketMutation.error?.message ||
                    'Please try again later'}
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4 flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={createTicketMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={
                  !selectedIssueType ||
                  !description.trim() ||
                  !email.trim() ||
                  createTicketMutation.isPending
                }
              >
                {createTicketMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Ticket'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SupportTicketModal
